// Google Maps API loader and utilities
import { getGoogleMapsConfig } from './googleMapsConfig';

let isLoading = false;
let isLoaded = false;
let loadPromise: Promise<void> | null = null;

export const loadGoogleMapsAPI = async (): Promise<void> => {
  if (isLoaded) {
    return Promise.resolve();
  }

  if (isLoading && loadPromise) {
    return loadPromise;
  }

  isLoading = true;
  
  loadPromise = new Promise<void>(async (resolve, reject) => {
    try {
      // Fetch configuration dynamically
      const config = await getGoogleMapsConfig();
      
      console.log('Google Maps config:', config);
      
      if (!config.apiKey) {
        console.warn('Google Maps API key not configured');
        reject(new Error('Google Maps API key not configured'));
        return;
      }

      if (!config.enabled) {
        console.warn('Google Maps is disabled');
        reject(new Error('Google Maps is disabled'));
        return;
      }

      // Check if already loaded
      if (window.google && window.google.maps) {
        isLoaded = true;
        isLoading = false;
        resolve();
        return;
      }

      // Create script element
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${config.apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        console.log('Google Maps script loaded successfully');
        isLoaded = true;
        isLoading = false;
        resolve();
      };

      script.onerror = (error) => {
        console.error('Failed to load Google Maps script:', error);
        isLoading = false;
        reject(new Error('Failed to load Google Maps API'));
      };

      document.head.appendChild(script);
    } catch (error) {
      isLoading = false;
      reject(error);
    }
  });

  return loadPromise;
};

export const isGoogleMapsLoaded = (): boolean => {
  return isLoaded && !!(window.google && window.google.maps);
};

export const geocodeAddress = async (address: string): Promise<{
  lat: number;
  lng: number;
  formatted_address: string;
} | null> => {
  await loadGoogleMapsAPI();
  
  return new Promise((resolve) => {
    const geocoder = new window.google.maps.Geocoder();
    
    geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        const location = results[0].geometry.location;
        resolve({
          lat: location.lat(),
          lng: location.lng(),
          formatted_address: results[0].formatted_address
        });
      } else {
        resolve(null);
      }
    });
  });
};

export const reverseGeocode = async (lat: number, lng: number): Promise<{
  formatted_address: string;
  address_components: google.maps.GeocoderAddressComponent[];
} | null> => {
  await loadGoogleMapsAPI();
  
  return new Promise((resolve) => {
    const geocoder = new window.google.maps.Geocoder();
    const latLng = { lat, lng };
    
    geocoder.geocode({ location: latLng }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        resolve({
          formatted_address: results[0].formatted_address,
          address_components: results[0].address_components
        });
      } else {
        resolve(null);
      }
    });
  });
};

export const getDistanceBetween = (
  lat1: number, 
  lng1: number, 
  lat2: number, 
  lng2: number
): number => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c; // Distance in kilometers
};